// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DrawerContent from "@mcmc/XDL/Components/Common/DrawerContent";
import DrawerFooter from "@mcmc/XDL/Components/Common/DrawerFooter";
import DrawerLink from "@mcmc/XDL/Components/Common/DrawerLink";
import LinkButton from "@mcmc/XDL/Components/Common/LinkButton";
import Account from "@mcmc/XDL/Icons/Account";
import Export from "@mcmc/XDL/Icons/Export";
import { Roboto } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => {
    const allowCreateAccount = getSettingsCollection(state).accountSettings.allowCreateAccount;
    const displayChangeCustomerLink = getSession(state).displayChangeCustomerLink;
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        isSigningIn: state.context.isSigningIn,
        userName: getSession(state).firstName || getSession(state).userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-changes
        createAccountUrl: allowCreateAccount ? getPageLinkByPageType(state, "CreateAccountPage")?.url : undefined,
        changeCustomerUrl: displayChangeCustomerLink
            ? getPageLinkByPageType(state, "ChangeCustomerPage")?.url
            : undefined,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ButtonPresentationProps;
    changeCustomerButton?: ButtonPresentationProps;
    signInButton?: ButtonPresentationProps;
    createAccountLink?: LinkPresentationProps;
    createAccountWrapper?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            color: ${getColor("navigation.secondary.text.main")};
            &:hover {
                ${/* sc-selector */ IconWrapper}, ${/* sc-selector */ TypographyStyle} {
                    opacity: 0.5;
                    color: ${getColor("navigation.secondary.text.hover")};
                }
            }
        `,
    },
    titleIcon: {
        width: 27,
        height: 31,
        color: "navigation.secondary.text.main",
    },
    titleTypography: {
        css: css`
            text-align: right;
            margin-right: 10px;
            max-width: 150px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
        ellipsis: true,
        variant: "headerTertiary",
        transform: "capitalize",
        color: "navigation.secondary.text.main",
    },
    signOutClickable: {
        sizeVariant: "medium",
        variant: "secondary",
    },
    changeCustomerButton: {
        sizeVariant: "medium",
    },
    signInButton: {
        color: "primary",
        typographyProps: {
            size: 14,
            weight: 700,
        },
        css: css`
            ${IconWrapper} {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 0;
                            &,
                            &&:not(:disabled):hover,
                            &&:not(:disabled):active {
                                background: transparent;
                                border-color: transparent;
                            }

                            ${IconWrapper} {
                                display: block;
                            }

                            ${TypographyStyle} {
                                display: none;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    createAccountWrapper: {
        css: css`
            margin-top: 20px;
            line-height: 20px;
        `,
    },
    createAccountLink: {
        typographyProps: {
            ...Roboto,
            lineHeight: "20px",
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            display: block;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    id,
    userName,
    isSignInPage,
    isSigningIn,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    createAccountUrl,
    changeCustomerUrl,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState(false);
    const [isMyAccountDrawerOpen, setIsMyAccountDrawerOpen] = useState(false);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    useEffect(() => {
        setIsSignInDrawerOpen(false);
        setIsMyAccountDrawerOpen(false);
    }, [currentLocation]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = Account;
    }

    const onSignInHandler = () => {
        setIsSignInDrawerOpen(true);
    };

    const onCreateAccountHandler = () => {
        if (createAccountUrl) {
            navigate(createAccountUrl);
        }
        setIsSignInDrawerOpen(false);
    };

    const navigate = (url: string) => {
        let returnUrl = `?returnUrl=${encodeURIComponent(
            currentLocation.pathname + currentLocation.search,
        )}&clientRedirect=true`;
        if (currentLocation.search.indexOf("returnUrl") > -1) {
            returnUrl = currentLocation.search;
        }

        history.push(`${url}${returnUrl}`);
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        setIsMyAccountDrawerOpen(false);
    };

    const myAccountHandler = () => {
        setIsMyAccountDrawerOpen(false);
        history.push(`${myAccountPageLink.url}`);
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {userName && !currentUserIsGuest && (
                <>
                    <Clickable
                        {...styles.titleClickable}
                        data-test-selector="header_signIn"
                        id="myaccountflyout"
                        onClick={includeAccountMenu ? () => setIsMyAccountDrawerOpen(true) : myAccountHandler}
                    >
                        {showLabel && (
                            <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                {translate("Hello, ")}
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {userName}
                                </span>
                            </Typography>
                        )}
                        {showIcon && includeAccountMenu && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                    </Clickable>
                    {!includeAccountMenu && (
                        <Clickable {...styles.titleClickable} data-test-selector="headerSignOutLink">
                            <Icon
                                {...styles.titleIcon}
                                src={Export}
                                title={isPunchOutSession ? translate("Exit PunchOut") : translate("Sign Out")}
                                onClick={onSignOutHandler}
                            />
                        </Clickable>
                    )}
                    <Drawer
                        position="right"
                        isOpen={isMyAccountDrawerOpen}
                        handleClose={() => setIsMyAccountDrawerOpen(false)}
                        headline={<DrawerLink onClick={myAccountHandler} title={translate("My Account")} />}
                    >
                        <DrawerContent>
                            <Zone contentId={id} zoneName="content" />
                        </DrawerContent>
                        <DrawerFooter>
                            {changeCustomerUrl && (
                                <LinkButton
                                    href={changeCustomerUrl}
                                    onClick={() => setIsMyAccountDrawerOpen(false)}
                                    {...styles.changeCustomerButton}
                                >
                                    {translate("Change Bill-To / Ship-To")}
                                </LinkButton>
                            )}
                            {userName && !currentUserIsGuest && (
                                <Button
                                    {...styles.signOutClickable}
                                    onClick={onSignOutHandler}
                                    data-test-selector="headerSignOutLink"
                                >
                                    {isPunchOutSession ? translate("Exit PunchOut") : translate("Sign Out")}
                                </Button>
                            )}
                        </DrawerFooter>
                    </Drawer>
                </>
            )}
            {(!userName || (userName && currentUserIsGuest)) && (
                <>
                    <Button data-test-selector="header_signIn" onClick={onSignInHandler} {...styles.signInButton}>
                        {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />}
                        <Typography {...styles.signInButton?.typographyProps}>{signInStatusText}</Typography>
                    </Button>
                    <Drawer
                        position="right"
                        isOpen={isSignInDrawerOpen}
                        handleClose={() => setIsSignInDrawerOpen(false)}
                        headline={
                            <DrawerLink
                                href={signInUrl}
                                onClick={() => setIsSignInDrawerOpen(false)}
                                title={translate("Sign In")}
                            />
                        }
                    >
                        <DrawerContent>
                            {createWidgetElement("SignIn/SignInExistingAccount", {
                                fields: { isHeaderSignIn: true, fullWidthButton: true },
                            })}
                            <StyledWrapper>
                                <Zone contentId={id} zoneName="externalProvidersZone" />
                            </StyledWrapper>
                            {createAccountUrl ? (
                                <StyledWrapper {...styles.createAccountWrapper}>
                                    {translate("Don’t have an account with us?")}
                                    <Link {...styles.createAccountLink} onClick={onCreateAccountHandler}>
                                        {translate("Create an Account")}
                                    </Link>
                                </StyledWrapper>
                            ) : null}
                        </DrawerContent>
                    </Drawer>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
        ],
        group: "Common",
    },
};

export default widgetModule;
